import { Box3, BufferGeometry, Group, Line, Mesh, Vector3, type Object3D } from 'three'
import { lowerCase } from 'es-toolkit'
import { v4 as uuid } from 'uuid'
import { defaultLineMaterial, defaultMeshMaterial, ThreeItemKeys, ThreeItemTypes, ThreeOriginalFileTypes } from '../../../constants/three'
import { setFileLoadingStatus, resetFileLoadingState } from '../../../stores/file-loading'
import { getOptimizationStrategy } from '../../../constants/loader-config'
import { deserializeTransferredObjects } from '../../../helpers/three/geometry-transfer'

class BaseFileLoader {
  protected base64File?: string
  protected arrayBuffer?: ArrayBuffer

  private MM_TO_M_SCALE_FACTOR = 0.001
  private UNIT_DETECTION_THRESHOLD_MM = 100
  private DEFAULT_LAYER_HEIGHT_MM = 20
  private DEFAULT_LAYERS_GROUP_AMOUNT = 1

  constructor(protected file: File) { }

  async load(): Promise<Group> {
    throw new Error('Not implemented')
  }

  protected getStandardMaterial() {
    const material = defaultMeshMaterial.clone()
    material.envMap = null
    material.envMapIntensity = 0
    material.needsUpdate = true
    material.color.convertSRGBToLinear()
    return material
  }

  protected getLineMaterial() {
    return defaultLineMaterial.clone()
  }

  protected async loadBase64() {
    this.base64File = await this.getBase64File()
  }

  protected async loadArrayBuffer() {
    this.arrayBuffer = await this.file.arrayBuffer()
  }

  private getBase64File() {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(this.file)
      reader.onload = () => resolve(reader.result as string)
      reader.onerror = reject
    })
  }

  protected addMeshesToScene(meshes: Object3D[], file: File, defaultOptions?: (mesh: Object3D, isGroup: boolean) => Record<string, string | number | boolean | undefined>) {
    const group = new Group()
    const groupId = uuid()
    group.name = file.name

    const sharedDefaultData = {
      [ThreeItemKeys.LAYER_HEIGHT]: this.DEFAULT_LAYER_HEIGHT_MM,
      [ThreeItemKeys.LAYERS_GROUP_AMOUNT]: this.DEFAULT_LAYERS_GROUP_AMOUNT,
      [ThreeItemKeys.TRANSLATION_LOCKED_X]: false,
      [ThreeItemKeys.TRANSLATION_LOCKED_Y]: false,
      [ThreeItemKeys.TRANSLATION_LOCKED_Z]: true,
      [ThreeItemKeys.ROTATION_LOCKED_X]: true,
      [ThreeItemKeys.ROTATION_LOCKED_Y]: true,
      [ThreeItemKeys.ROTATION_LOCKED_Z]: false,
    }

    for (let i = 0; i < meshes.length; i++) {
      const mesh = this.centerPivot(meshes[i])
      mesh.name = `${file.name} (${i + 1} / ${meshes.length})`

      const defaultOptionsData = defaultOptions?.(mesh, false)
      this.saveObjectOriginalMaterial(mesh)
      const userData = {
        ...sharedDefaultData,
        ...defaultOptionsData,
        ...mesh.userData,
        [ThreeItemKeys.ITEM_TYPE]: ThreeItemTypes.FILE,
        [ThreeItemKeys.FILE_NAME]: mesh.name,
        [ThreeItemKeys.FILE_ID]: uuid(),
        [ThreeItemKeys.ASSIGNED_GROUP_ID]: groupId,
      }

      if (defaultOptionsData && defaultOptionsData[ThreeItemKeys.ORIGINAL_FILE_TYPE] === ThreeOriginalFileTypes.DXF_2D) {
        const parentGroup = new Group()
        parentGroup.name = mesh.name
        parentGroup.userData = userData
        // Copy the mesh position to the parent group and adjust mesh position relative to it
        parentGroup.position.copy(mesh.position)
        mesh.position.set(0, 0, 0)
        mesh.userData = { [ThreeItemKeys.IS_ORIGINAL_DXF]: true }
        parentGroup.add(mesh)
        group.add(parentGroup)
      }
      else {
        mesh.userData = userData
        group.add(mesh)
      }
    }

    group.userData = {
      ...sharedDefaultData,
      ...defaultOptions?.(group, true),
      [ThreeItemKeys.LOCKED]: true,
      [ThreeItemKeys.ITEM_TYPE]: ThreeItemTypes.GROUP,
      [ThreeItemKeys.FILE_NAME]: file.name,
      [ThreeItemKeys.GROUP_ID]: groupId,
    }

    // Center the main group at the barycentre of all its children
    if (group.children.length > 0) {
      const boundingBox = this.getBoundingBox(group)
      const groupCenter = new Vector3(
        (boundingBox.min.x + boundingBox.max.x) / 2,
        (boundingBox.min.y + boundingBox.max.y) / 2,
        (boundingBox.min.z + boundingBox.max.z) / 2,
      )

      // Position the group at its barycentre
      group.position.copy(groupCenter)

      // Adjust all children positions to be relative to the group center
      group.children.forEach((child) => {
        child.position.sub(groupCenter)
      })
    }

    group.traverse((obj) => {
      if (obj instanceof Mesh) {
        obj.geometry.computeVertexNormals()
        obj.geometry.normalizeNormals()
      }
    })

    return group
  }

  private centerPivot(object: Object3D): Object3D {
    switch (object.type) {
      case 'Group':
        return this.centerPivotGroup(object as Group)
      case 'Mesh':
        return this.centerPivotMesh(object as Mesh)
      case 'Line':
        return this.centerPivotLine(object as Line)
      default:
        console.warn(`Did not center pivot for ${object.type}`)
        return object.clone()
    }
  }

  private translateGeometry(geometry: BufferGeometry, translation: Vector3): BufferGeometry {
    const g = geometry.clone()
    g.translate(-translation.x, -translation.y, -translation.z)
    return g
  }

  private centerPivotMesh(mesh: Mesh): Mesh {
    const newMesh = mesh.clone()
    const boundingBox = this.getBoundingBox(newMesh)
    const center = new Vector3(
      (boundingBox.min.x + boundingBox.max.x) / 2,
      (boundingBox.min.y + boundingBox.max.y) / 2,
      (boundingBox.min.z + boundingBox.max.z) / 2,
    )

    // Move the geometry so its center is at the local origin
    newMesh.geometry = this.translateGeometry(newMesh.geometry, center)
    newMesh.geometry.computeVertexNormals()
    newMesh.geometry.normalizeNormals()

    // Move the mesh to compensate and maintain the same world position
    newMesh.position.copy(center)

    return newMesh
  }

  private centerPivotLine(line: Line): Line {
    const newLine = line.clone()
    const boundingBox = this.getBoundingBox(newLine)
    const center = new Vector3(
      (boundingBox.min.x + boundingBox.max.x) / 2,
      (boundingBox.min.y + boundingBox.max.y) / 2,
      (boundingBox.min.z + boundingBox.max.z) / 2,
    )

    // Move the geometry so its center is at the local origin
    newLine.geometry = this.translateGeometry(newLine.geometry, center)

    // Move the line to compensate and maintain the same world position
    newLine.position.copy(center)

    return newLine
  }

  private centerPivotGroup(group: Group): Group {
    // If the group has only one child, return the centered child directly
    if (group.children.length === 1) {
      return this.centerPivot(group.children[0]) as Group
    }

    // First, center each child's pivot
    const centeredChildren: Object3D[] = []
    for (const child of group.children) {
      centeredChildren.push(this.centerPivot(child))
    }

    // Create a temporary group to calculate the bounding box of all children
    const tempGroup = new Group()
    for (const child of centeredChildren) {
      tempGroup.add(child)
    }

    // Calculate the center of all children
    const boundingBox = this.getBoundingBox(tempGroup)
    const groupCenter = new Vector3(
      (boundingBox.min.x + boundingBox.max.x) / 2,
      (boundingBox.min.y + boundingBox.max.y) / 2,
      (boundingBox.min.z + boundingBox.max.z) / 2,
    )

    // Create the final group positioned at the center
    const newGroup = new Group()
    newGroup.position.copy(groupCenter)

    // Remove children from temp group and adjust their positions relative to group center
    tempGroup.clear()
    for (const child of centeredChildren) {
      child.position.sub(groupCenter)
      newGroup.add(child)
    }

    return newGroup
  }

  private saveObjectOriginalMaterial(object: Object3D) {
    switch (object.type) {
      case 'Mesh':
      case 'Line':
        this.cloneOriginalMaterial(object as Mesh)
        break
      default:
        object.traverse(item => this.cloneOriginalMaterial(item as Mesh))
        break
    }
  }

  private cloneOriginalMaterial(mesh: Mesh) {
    if (mesh.material && 'clone' in mesh.material) {
      mesh.userData.originalMaterial = mesh.material.clone().toJSON()
    }
  }

  private getBoundingBox(object: Object3D) {
    return new Box3().setFromObject(object)
  }

  /**
   * Scale down an object to appropriate size for the application.
   * If scale is not provided, automatically determines scale based on bounding box size.
   *
   * Auto-scale logic:
   * - If max dimension > 100 units: apply 0.001 scale (mm to m conversion)
   * - Otherwise: no scaling needed (already in correct units)
   *
   * @param object - The 3D object to scale
   */
  protected scaleDownObject(object: Object3D, scale?: number) {
    if (scale === undefined) {
      scale = this.getAutoScaleFactor(object)
    }

    switch (object.type) {
      case 'Group':
        return this.scaleDownGroup(object as Group, scale)
      case 'Mesh':
        return this.scaleDownMesh(object as Mesh, scale)
      case 'Line':
        return this.scaleDownLine(object as Line, scale)
      default:
        return object
    }
  }

  private getAutoScaleFactor(object: Object3D): number {
    const bbox = this.getBoundingBox(object)
    const size = new Vector3()
    bbox.getSize(size)

    const maxDimension = Math.max(size.x, size.y, size.z)

    if (maxDimension > this.UNIT_DETECTION_THRESHOLD_MM) {
      return this.MM_TO_M_SCALE_FACTOR
    }
    else {
      return 1
    }
  }

  private scaleDownGroup(object: Group, scale: number) {
    const newGroupChildren: Object3D[] = []

    for (const child of object.children) {
      newGroupChildren.push(this.scaleDownObject(child, scale))
    }

    object.children = newGroupChildren
    return object
  }

  private scaleDownMesh(object: Mesh, scale: number) {
    // Scale the vertex positions in place so the index buffer is preserved.
    // Indexed geometries (e.g. STEP via occt, 3MF) define their triangles
    // through the index; rebuilding from the raw position array would drop it
    // and scramble the mesh into garbage triangles.
    object.geometry.scale(scale, scale, scale)
    object.geometry.deleteAttribute('normal')
    object.geometry.computeVertexNormals()
    object.geometry.normalizeNormals()

    return object
  }

  private scaleDownLine(object: Line, scale: number) {
    const positions = object.geometry.attributes.position.array
    const newPoints: Vector3[] = []

    for (let i = 0; i < positions.length; i += 3) {
      newPoints.push(
        new Vector3(
          positions[i] * scale,
          positions[i + 1] * scale,
          positions[i + 2] * scale,
        ),
      )
    }

    object.geometry = new BufferGeometry().setFromPoints(newPoints)

    return object
  }

  // ============================================================================
  // Web Worker Support
  // ============================================================================

  /**
   * Check if this file should be processed in a Web Worker
   */
  protected shouldUseWorker(): boolean {
    const strategy = getOptimizationStrategy(this.file.size)
    return strategy.useWorker
  }

  /**
   * Get optimization strategy for this file
   */
  protected getOptimizationStrategy() {
    return getOptimizationStrategy(this.file.size)
  }

  /**
   * Get the file type identifier for the worker
   * Override in subclasses to specify the file type
   */
  protected getWorkerFileType(): FileParserSupportedType | null {
    return null
  }

  /**
   * Process the file using a Web Worker
   * Returns parsed Object3D groups that still need material assignment and scaling
   */
  protected async loadWithWorker(): Promise<Object3D[]> {
    if (!this.arrayBuffer) {
      await this.loadArrayBuffer()
    }

    const fileType = this.getWorkerFileType()
    if (!fileType) {
      throw new Error('Worker file type not specified')
    }

    return new Promise((resolve, reject) => {
      const worker = new Worker(
        new URL('../../../workers/file-parser.worker.ts', import.meta.url),
        { type: 'module' },
      )

      worker.onmessage = (event: MessageEvent<FileParserWorkerMessage>) => {
        const message = event.data

        switch (message.type) {
          case 'progress':
            setFileLoadingStatus(message.status, this.file.name, message.progress)
            break

          case 'result': {
            worker.terminate()

            // Rebuild Three.js objects from the transferred geometry buffers (zero-copy).
            const entities = deserializeTransferredObjects(message.objects)
            resolve(entities)
            break
          }

          case 'error':
            worker.terminate()
            reject(new Error(message.message))
            break
        }
      }

      worker.onerror = (error) => {
        worker.terminate()
        reject(error)
      }

      // Transfer the ArrayBuffer to the worker (zero-copy)
      const input: FileParserWorkerInput = {
        fileType,
        arrayBuffer: this.arrayBuffer!,
        fileName: this.file.name,
        fileSize: this.file.size,
      }

      worker.postMessage(input, [this.arrayBuffer!])
      this.arrayBuffer = undefined // ArrayBuffer is now owned by worker
    })
  }

  /**
   * Wrapper for load operations with automatic progress tracking
   */
  protected async loadWithProgress<T>(operation: () => Promise<T>): Promise<T> {
    setFileLoadingStatus('reading', this.file.name, 0)

    try {
      const result = await operation()
      resetFileLoadingState()
      return result
    }
    catch (error) {
      resetFileLoadingState()
      throw error
    }
  }

  protected getFileExtension() {
    return lowerCase(this.file.name.split('.').at(-1) ?? '').replace(/ /g, '')
  }
}

export default BaseFileLoader
